// The two controls that slice a view: a segmented control and the
// tri-state multi-select used wherever a table can be filtered.

import { useState } from "react";
import { toggled } from "@/platform/kit";
import { cx } from "@/platform/ui/primitives/cx.js";
import { Popover } from "@/platform/ui/primitives/popover.js";

// ---- SegmentedControl ----
/** The grid that lays options out in rows of equal segments: its column
 *  count, and each option's span. */
function rowGrid(rows: readonly number[]) {
  const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);
  const columns = rows.reduce((l, n) => (l * n) / gcd(l, n), 1);
  return { columns, spans: rows.flatMap((n) => Array<number>(n).fill(columns / n)) };
}

/** Options side by side at their own width, wrapping only where the column
 *  is too narrow for them. */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  size = "md",
  full = false,
  rows,
  label,
  describedBy,
  className,
}: {
  /** An option `disabled` stays in its place, greyed, and cannot be chosen. */
  options: { value: T; label: string; disabled?: boolean }[];
  value: T;
  onChange: (v: T) => void;
  size?: "compact" | "md";
  full?: boolean;
  /** In place of wrapping, below a 32rem container: full-width rows of
   *  equal segments, this many options to each (`[4, 3]`). */
  rows?: readonly number[];
  /** What the choice is, for assistive technology, where nothing on screen
   *  names it. */
  label?: string;
  /** The id of what describes the choice. */
  describedBy?: string;
  className?: string;
}) {
  const grid = rows && rowGrid(rows);
  return (
    <div
      {...(label ? { role: "group", "aria-label": label } : {})}
      aria-describedby={describedBy}
      className={cx(
        grid ? "grid @lg:inline-flex" : full ? "flex" : "inline-flex",
        "max-w-full shrink-0 flex-wrap rounded-lg border border-line bg-surface p-0.5",
        className,
      )}
      style={grid ? { gridTemplateColumns: `repeat(${grid.columns}, minmax(0, 1fr))` } : undefined}
    >
      {options.map((o, i) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          disabled={o.disabled}
          onClick={() => onChange(o.value)}
          style={grid ? { gridColumn: `span ${grid.spans[i]}` } : undefined}
          className={cx(
            "whitespace-nowrap rounded-md font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40",
            full && "flex-1",
            size === "compact"
              ? full
                ? "px-1 py-1 text-[10.5px]"
                : "px-2 py-1 text-[11px]"
              : "px-3 py-1.5 text-xs",
            value === o.value ? "bg-accent text-bg" : "text-muted hover:text-ink",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---- MultiSelectFilter ----
// The one multi-select filter dropdown used everywhere a table can be sliced
// by broker/account/category (Portfolio positions, Trades & Dividends,
// Transactions): a button that opens a checkbox list, with an Excel-style
// tri-state "All" checkbox on top — checked/unchecked toggles every option at
// once, a dash means only some are selected. `selected === null` means "all".
interface FilterOption {
  value: string;
  label: string;
}

export function MultiSelectFilter({
  label,
  options,
  selected,
  onChange,
  align = "end",
}: {
  label: string;
  options: FilterOption[];
  selected: Set<string> | null;
  onChange: (next: Set<string> | null) => void;
  /** The button's edge the list lines up with: `start` where the button
   *  leads a toolbar on the left. */
  align?: "start" | "end";
}) {
  const [open, setOpen] = useState(false);
  const isAll = selected == null;
  const has = (v: string) => isAll || selected!.has(v);
  const toggle = (v: string) => {
    const next = toggled(isAll ? new Set(options.map((o) => o.value)) : selected!, v);
    onChange(next.size === options.length ? null : next);
  };
  const selectedCount = isAll ? options.length : selected!.size;
  const allState: "all" | "none" | "some" =
    isAll || selectedCount === options.length ? "all" : selectedCount === 0 ? "none" : "some";
  const toggleAll = () => onChange(allState === "all" ? new Set() : null);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align={align}
      panelClassName="max-h-64 w-52 overflow-auto overscroll-contain rounded-lg border border-line bg-surface p-1.5 shadow-lg"
      trigger={
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="rounded-md border border-line px-2.5 py-1 text-[10px] font-bold text-dim hover:text-ink"
        >
          {label} · {isAll ? "All" : `${selectedCount}/${options.length}`} ▾
        </button>
      }
    >
      <label className="mb-1.5 flex cursor-pointer items-center gap-2 border-b border-line/60 px-1.5 pb-1.5 text-[11px] font-bold text-dim hover:text-ink">
        <input
          type="checkbox"
          checked={allState === "all"}
          ref={(el) => {
            if (el) el.indeterminate = allState === "some";
          }}
          onChange={toggleAll}
        />
        All
      </label>
      {options.map((o) => (
        <label
          key={o.value}
          className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-[11px] text-dim hover:bg-surface-2"
        >
          <input type="checkbox" checked={has(o.value)} onChange={() => toggle(o.value)} />
          <span className="truncate" title={o.label}>
            {o.label}
          </span>
        </label>
      ))}
    </Popover>
  );
}
